import * as React from 'react';

import { surveyApi } from '../api';
import type { IFinding, IProgress, ISnapshotUsage } from '../api/contracts';
import { formatDay } from '../format';
import { FindingsPanel, mergeCellFindings } from '../runtime';

/**
 * What stands between the respondent and submitting, beside the form (the two-portals design,
 * §3.3): a column at 1440 px, a strip above the form below that.
 *
 * **Counts the page already holds, restated — never a verdict.** "n of m required answers" is
 * survey-api's count (`state.progress`, the survey review's G6): the engine's, never this app's,
 * since requiredness moves with the rules and a count made here would drift from the gate that
 * decides submit. "Needs attention" is the hard
 * findings the form is showing, the same ones that flag the section list; "To confirm before you
 * submit" is survey-api's `engine.unconfirmed_proposal` findings, shown or not — a registry's
 * answer its submit gate refuses until confirmed or changed (the survey review plan's D5); "Filled
 * in for you, not yet checked" is the rest of `pendingPrefill`, which holds nothing; the closing
 * date is the cycle's `closesAt` as the catalogue sent it. None of them decides whether submit is
 * offered. What is answered is survey-api's to count, never a count of clean questions made here:
 * the page holds a finding back until the respondent reaches it (`visibleFindings`), so clean
 * would mean unreached.
 *
 * The response-level findings ("About this survey") belong to the survey, not to the step on
 * screen.
 */
export function SurveyStatus({
  findings,
  globalFindings,
  progress,
  toConfirm,
  unconfirmed,
  closesAt,
  usageOf,
  onAcknowledge
}: {
  /** Every finding the form is showing. */
  findings: IFinding[];
  globalFindings: IFinding[];
  /** survey-api's count of required answers; absent from a server before SR-469. */
  progress: IProgress | undefined;
  /** Registry proposals survey-api holds submit on; `0` where the response cannot be written. */
  toConfirm: number;
  /** The other proposals still unconfirmed; `0` where the response cannot be written. */
  unconfirmed: number;
  closesAt: Nullable<string>;
  usageOf: (usageCode: string) => ISnapshotUsage | undefined;
  onAcknowledge: (finding: IFinding) => void;
}) {
  // A table row's missing cells count once, as the row line says them (B4-7).
  const attention = mergeCellFindings(findings).filter(
    (finding) => finding.severity === 'hard'
  ).length;

  return (
    <div className="grid gap-4 min-[90rem]:sticky min-[90rem]:top-[5.5rem]">
      <section
        aria-labelledby="survey-status-title"
        className="rounded-[0.625rem] border border-border bg-card px-5 py-4 min-[90rem]:py-5"
      >
        <h2
          id="survey-status-title"
          className="mb-2 text-[0.9375rem] font-medium tracking-normal text-[var(--color-royal-blue)]"
        >
          Before you submit
        </h2>
        {progress && progress.required > 0 && (
          <div className="mb-2 grid gap-1.5">
            <p className="text-sm text-[var(--color-midnight-blue)] tabular-nums" data-progress>
              {progress.answered} of {progress.required} required answers
            </p>
            <div aria-hidden className="h-1.5 overflow-hidden rounded-full bg-[var(--color-slight-blue)]">
              <div
                className="h-full rounded-full bg-[var(--color-sapphire)] transition-[width]"
                style={{ width: `${Math.min(100, (100 * progress.answered) / progress.required)}%` }}
              />
            </div>
          </div>
        )}
        <dl className="grid gap-x-8 text-sm sm:grid-cols-[repeat(auto-fit,minmax(12rem,1fr))] min-[90rem]:grid-cols-1 min-[90rem]:divide-y min-[90rem]:divide-border">
          <StatusLine label="Needs attention" value={String(attention)} />
          {toConfirm > 0 && (
            <StatusLine
              label="To confirm before you submit"
              value={String(toConfirm)}
              data={{ 'data-prefill-blocking': toConfirm }}
            />
          )}
          {unconfirmed > 0 && (
            <StatusLine
              label="Filled in for you, not yet checked"
              value={String(unconfirmed)}
              data={{ 'data-prefill-unconfirmed': unconfirmed }}
            />
          )}
          {closesAt && <StatusLine label="Closes" value={formatDay(closesAt)} />}
        </dl>
      </section>

      {globalFindings.length > 0 ? (
        <FindingsPanel
          findings={globalFindings}
          title="About this survey"
          usageOf={usageOf}
          onAcknowledge={onAcknowledge}
          className="mt-0"
        />
      ) : (
        <section className="hidden rounded-[0.625rem] border border-border bg-card px-5 py-5 min-[90rem]:block">
          <h2 className="mb-2 text-[0.9375rem] font-medium tracking-normal text-[var(--color-royal-blue)]">
            About this survey
          </h2>
          <p className="text-sm leading-relaxed text-muted-foreground">
            Notes the survey's checks make about the whole survey, rather than one question,
            appear here. There are none.
          </p>
        </section>
      )}
    </div>
  );
}

function StatusLine({
  label,
  value,
  data
}: {
  label: string;
  value: string;
  data?: Record<`data-${string}`, string | number>;
}) {
  return (
    <div
      {...data}
      className="flex items-baseline justify-between gap-4 py-1.5"
    >
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium text-[var(--color-midnight-blue)] tabular-nums">{value}</dd>
    </div>
  );
}

/** Whose a response is and when its cycle closes — what the catalogue says about it. */
export interface IResponseListing {
  participantName: string;
  closesAt: Nullable<string>;
}

/**
 * The catalogue's row for one response, read beside the form for the two facts the state
 * document does not carry: whose response it is, and when its cycle closes.
 *
 * `GET /my/cycles` is the read Home and My surveys already make, through the same function; the
 * row is found by the response id and nothing about it is inferred. It gates nothing, so a read
 * that fails — or a response the catalogue does not list — leaves the bar without a name and the
 * status without a date, and is logged rather than said: the survey itself is unaffected.
 */
export function useResponseListing(responseId: Nullable<string>): Nullable<IResponseListing> {
  const [listing, setListing] = React.useState<Nullable<IResponseListing>>(null);

  React.useEffect(() => {
    setListing(null);
    if (!responseId) return undefined;
    const controller = new AbortController();

    void surveyApi
      .myCycles(controller.signal)
      .then((cycles) => {
        for (const cycle of cycles) {
          const response = cycle.responses.find((row) => row.responseId === responseId);
          if (response) {
            setListing({ participantName: response.participantName, closesAt: cycle.closesAt });
            return;
          }
        }
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        console.error('[Survey] Could not read the catalogue beside the form:', error);
      });

    return () => controller.abort();
  }, [responseId]);

  return listing;
}
